'use client';

import { c as _c } from "react/compiler-runtime";
import type { User } from '@supabase/supabase-js';
import { useMemo } from 'react';
import type { DesktopReviewQueue } from './review-queue';
import { buildDesktopReviewQueueSections } from './review-queue';
import { useInboxV2CheckSummaries } from '@/data/pull-requests/use-inbox-v2-check-summaries';
import { useInboxV2OpenPullRequests } from '@/data/pull-requests/use-inbox-v2-pull-requests';
import { useInboxV2Snoozes } from '@/data/pull-requests/use-inbox-v2-snoozes';
import { isSessionExpiredError } from '@/lib/auth/handle-session-expired';
import { useAuth } from '@/providers/auth-provider';
function getUsername(user: User | null): string {
  const username = user?.user_metadata?.user_name;
  return typeof username === 'string' ? username : '';
}

/** Reads the signed-in user's review queue from the open inbox pull requests. */
export function useDesktopReviewQueue() {
  const $ = _c(24);
  const {
    isLoading: isAuthLoading,
    user
  } = useAuth();
  let t0;
  if ($[0] !== user) {
    t0 = getUsername(user);
    $[0] = user;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const username = t0;
  const t1 = Boolean(username);
  let t2;
  if ($[2] !== t1) {
    t2 = {
      enabled: t1,
      refetchOnMount: true
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data,
    error,
    hasSelectedRepositories,
    isError,
    isLoading,
    isRepositoryScopeError,
    isRepositoryScopeLoading
  } = useInboxV2OpenPullRequests(t2);
  const t3 = Boolean(username);
  let t4;
  if ($[4] !== t3) {
    t4 = {
      enabled: t3
    };
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const {
    snoozes,
    isLoading: isLoadingSnoozes
  } = useInboxV2Snoozes(t4);
  const t5 = Boolean(username);
  let t6;
  if ($[6] !== t5) {
    t6 = {
      enabled: t5,
      refetchOnMount: true
    };
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const {
    data: checks,
    isError: isChecksError,
    isLoading: isLoadingChecks
  } = useInboxV2CheckSummaries("open", t6);
  let t7;
  bb0: {
    if (isAuthLoading) {
      let t8;
      if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = {
          status: "loading"
        };
        $[8] = t8;
      } else {
        t8 = $[8];
      }
      t7 = t8;
      break bb0;
    }
    if (!username || isSessionExpiredError(error)) {
      let t8;
      if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = {
          status: "signed-out"
        };
        $[9] = t8;
      } else {
        t8 = $[9];
      }
      t7 = t8;
      break bb0;
    }
    if (isError) {
      let t8;
      if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = {
          status: "error"
        };
        $[10] = t8;
      } else {
        t8 = $[10];
      }
      t7 = t8;
      break bb0;
    }
    if (isRepositoryScopeLoading || isLoadingSnoozes || isLoadingChecks) {
      let t8;
      if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = {
          status: "loading"
        };
        $[11] = t8;
      } else {
        t8 = $[11];
      }
      t7 = t8;
      break bb0;
    }
    if (data) {
      let t8;
      if ($[12] !== checks || $[13] !== data.pullRequests || $[14] !== data.stackContexts || $[15] !== isChecksError || $[16] !== snoozes || $[17] !== username) {
        t8 = buildDesktopReviewQueueSections(data.pullRequests, username, {
          stackContexts: data.stackContexts,
          checks: isChecksError ? {} : checks ?? {},
          snoozes
        });
        $[12] = checks;
        $[13] = data.pullRequests;
        $[14] = data.stackContexts;
        $[15] = isChecksError;
        $[16] = snoozes;
        $[17] = username;
        $[18] = t8;
      } else {
        t8 = $[18];
      }
      let t9;
      if ($[19] !== t8) {
        t9 = {
          status: "ready",
          ...t8
        };
        $[19] = t8;
        $[20] = t9;
      } else {
        t9 = $[20];
      }
      t7 = t9;
      break bb0;
    }
    if (isLoading || hasSelectedRepositories) {
      let t8;
      if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = {
          status: "loading"
        };
        $[21] = t8;
      } else {
        t8 = $[21];
      }
      t7 = t8;
      break bb0;
    }
    let t8;
    if ($[22] !== isRepositoryScopeError) {
      t8 = isRepositoryScopeError ? {
        status: "error"
      } : {
        status: "ready",
        open: [],
        folded: []
      };
      $[22] = isRepositoryScopeError;
      $[23] = t8;
    } else {
      t8 = $[23];
    }
    t7 = t8;
  }
  return t7;
}